import React from 'react';
import DynamicMultiplicationTable from './DynamicMultiplicationTable';
import DynamicSquaresTable from './DynamicSquaresTable';
import { CategoryDynamicComponent } from '../../types/dynamicComponents';
import { MULTIPLICATION_TABLE_COMPONENT_TYPE, SQUARES_TABLE_COMPONENT_TYPE } from './definitions';

interface DynamicComponentRendererProps {
  components?: CategoryDynamicComponent[];
  placementType: number;
  selections: Record<number, number>;
  onSelectionChange: (componentId: number, value: number) => void;
}

interface RegisteredComponentProps {
  component: CategoryDynamicComponent;
  selectedNumber: number | undefined;
  onSelectionChange: (componentId: number, value: number) => void;
}

const dynamicComponentRegistry: Record<string, React.FC<RegisteredComponentProps>> = {
  [MULTIPLICATION_TABLE_COMPONENT_TYPE]: DynamicMultiplicationTable,
  [SQUARES_TABLE_COMPONENT_TYPE]: DynamicSquaresTable,
};

const DynamicComponentRenderer: React.FC<DynamicComponentRendererProps> = ({
  components,
  placementType,
  selections,
  onSelectionChange,
}) => {
  if (!components || components.length === 0) {
    return null;
  }

  const componentsForPlacement = components
    .filter((component) => component.placementType === placementType)
    .sort((first, second) => first.displayOrder - second.displayOrder);

  if (componentsForPlacement.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {componentsForPlacement.map((component) => {
        const componentTypeKey = component.componentType?.toLowerCase();
        const RegisteredComponent = componentTypeKey ? dynamicComponentRegistry[componentTypeKey] : undefined;

        if (!RegisteredComponent) {
          const introHtmlValue = component.introHtml?.trim();
          const captionOverrideValue = component.captionOverride?.trim();
          return (
            <div key={component.id} className="border border-gray-200 rounded-lg bg-white p-3">
              {introHtmlValue && (
                <div
                  className="html-content text-sm text-gray-700 leading-relaxed text-left mb-3"
                  dangerouslySetInnerHTML={{ __html: introHtmlValue }}
                />
              )}
              <div className="text-sm text-gray-700 font-semibold">{component.name}</div>
              <div className="text-xs text-gray-500">Unsupported component type: {component.componentType}</div>
              {captionOverrideValue && <p className="mt-2 mb-0 text-xs text-gray-600 text-center">{captionOverrideValue}</p>}
            </div>
          );
        }

        return (
          <RegisteredComponent
            key={component.id}
            component={component}
            selectedNumber={selections[component.id]}
            onSelectionChange={onSelectionChange}
          />
        );
      })}
    </div>
  );
};

export default DynamicComponentRenderer;
